import React, { useEffect, useState, useCallback } from 'react';
import {
  ArrowPathIcon,
  PlusIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
  XCircleIcon,
  DocumentIcon,
  LinkIcon,
  ArrowTopRightOnSquareIcon,
  UserGroupIcon,
  CloudArrowUpIcon,
} from '@heroicons/react/24/solid';
import {
  fetchVendorUploads,
  fetchVendorTokens,
  createVendorToken,
  deactivateVendorToken,
} from '../services/vendorUploadAdminApi';
import Alert from './Alert';
import Confirm from './Confirm';
import PermissionGate from './PermissionGate';

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function shortenToken(token) {
  if (!token) return '—';
  return token.length > 12 ? `${token.slice(0, 8)}…${token.slice(-4)}` : token;
}

const STATUS_CONFIG = {
  completed: {
    label: 'Completed',
    icon: CheckCircleIcon,
    color: 'text-green-600',
    bg: 'bg-green-100',
  },
  initiated: {
    label: 'In Progress',
    icon: ClockIcon,
    color: 'text-blue-600',
    bg: 'bg-blue-100',
  },
  aborted: {
    label: 'Cancelled',
    icon: XCircleIcon,
    color: 'text-gray-500',
    bg: 'bg-gray-100',
  },
  error: {
    label: 'Failed',
    icon: ExclamationCircleIcon,
    color: 'text-red-600',
    bg: 'bg-red-100',
  },
};

const VENDOR_UPLOAD_ORIGIN = 'https://tv.gottea.in';

function getTokenFromUploadLink(uploadLink) {
  if (!uploadLink) return '';
  try {
    return new URL(uploadLink).searchParams.get('token') || uploadLink;
  } catch {
    return uploadLink;
  }
}

function getVendorUploadLink(uploadLinkOrToken) {
  const token = getTokenFromUploadLink(uploadLinkOrToken);
  if (!token) return '';
  return `${VENDOR_UPLOAD_ORIGIN}/uploadfile?token=${encodeURIComponent(token)}`;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function VendorUploadAdmin() {
  const [tab, setTab] = useState('uploads'); // 'uploads' | 'tokens'
  const [uploads, setUploads] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [alertMsg, setAlertMsg] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);
  const [filterToken, setFilterToken] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [copiedLink, setCopiedLink] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (tab === 'uploads') {
        const data = await fetchVendorUploads(filterToken || undefined);
        setUploads(data);
      } else {
        const data = await fetchVendorTokens();
        setTokens(data);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [tab, filterToken]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDeactivateToken = async (token) => {
    setConfirmAction({
      message: 'Deactivate this token? The vendor will no longer be able to upload files with this link.',
      onConfirm: async () => {
        try {
          await deactivateVendorToken(token);
          setAlertMsg('Token deactivated successfully.');
          loadData();
        } catch (e) {
          setError(e.message);
        }
      },
    });
  };

  const handleCopyLink = (link) => {
    navigator.clipboard.writeText(link);
    setCopiedLink(link);
    setTimeout(() => setCopiedLink(''), 2000);
  };

  // Derived stats
  const completedCount = uploads.filter((u) => u.status === 'completed').length;
  const inProgressCount = uploads.filter(
    (u) => u.status === 'initiated'
  ).length;
  const activeTokens = tokens.filter((t) => t.active !== false).length;

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Vendor Uploads</h1>
          <p className="text-sm text-gray-500 mt-1">
            View files uploaded by vendors and manage access tokens.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadData}
            className="p-2 text-teal-500 hover:bg-teal-50 rounded-lg transition-colors"
            title="Refresh"
          >
            <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {tab === 'tokens' && (
            <PermissionGate permission="admin.vendor_uploads.manage_tokens"><button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-1.5 px-4 py-2 bg-teal-500 text-white text-sm font-semibold rounded-lg hover:opacity-90 transition-opacity"
            >
              <PlusIcon className="w-4 h-4" />
              New Token
            </button></PermissionGate>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard
          icon={CloudArrowUpIcon}
          label="Total Uploads"
          value={String(uploads.length)}
        />
        <StatCard
          icon={CheckCircleIcon}
          label="Completed"
          value={String(completedCount)}
          color="text-green-600"
        />
        <StatCard
          icon={ClockIcon}
          label="In Progress"
          value={String(inProgressCount)}
          color="text-blue-600"
        />
        <StatCard
          icon={UserGroupIcon}
          label="Active Tokens"
          value={String(activeTokens)}
          color="text-teal-500"
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        <TabButton
          active={tab === 'uploads'}
          onClick={() => setTab('uploads')}
          label="Uploaded Files"
        />
        <TabButton
          active={tab === 'tokens'}
          onClick={() => setTab('tokens')}
          label="Access Tokens"
        />
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg p-3">
          <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Alert */}
      {alertMsg && (
        <div className="mb-4">
          <Alert
            message={alertMsg}
            onClose={() => setAlertMsg('')}
          />
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
        </div>
      ) : tab === 'uploads' ? (
        <UploadsTable
          uploads={uploads}
          filterToken={filterToken}
          setFilterToken={setFilterToken}
          onCopyLink={handleCopyLink}
          copiedLink={copiedLink}
        />
      ) : (
        <TokensTable
          tokens={tokens}
          onDeactivate={handleDeactivateToken}
          onCopyLink={handleCopyLink}
          copiedLink={copiedLink}
        />
      )}

      {/* Confirm dialog */}
      {confirmAction && (
        <Confirm
          message={confirmAction.message}
          onConfirm={() => {
            confirmAction.onConfirm();
            setConfirmAction(null);
          }}
          onCancel={() => setConfirmAction(null)}
        />
      )}

      {/* Create token modal */}
      {showCreateModal && (
        <CreateTokenModal
          onClose={() => setShowCreateModal(false)}
          onCreated={({ vendorName, uploadLink }) => {
            setAlertMsg(
              <div className="space-y-2">
                <p>{`Token created for "${vendorName}". Share link:`}</p>
                <a
                  href={uploadLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block text-teal-600 font-medium underline underline-offset-2"
                >
                  {uploadLink}
                </a>
              </div>
            );
            setShowCreateModal(false);
            loadData();
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stat card
// ---------------------------------------------------------------------------
function StatCard({ icon: Icon, label, value, color = 'text-gray-800' }) {
  return (
    <div className="bg-gt-white rounded-xl shadow-sm p-4 flex items-center gap-3">
      <div className="p-2 bg-gray-50 rounded-lg">
        <Icon className={`w-6 h-6 ${color}`} />
      </div>
      <div>
        <div className={`text-xl font-bold ${color}`}>{value}</div>
        <div className="text-xs text-gray-500">{label}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab button
// ---------------------------------------------------------------------------
function TabButton({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
        active
          ? 'border-teal-500 text-teal-500'
          : 'border-transparent text-gray-500 hover:text-gray-700'
      }`}
    >
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Uploads table
// ---------------------------------------------------------------------------
function UploadsTable({ uploads, filterToken, setFilterToken, onCopyLink, copiedLink }) {
  if (uploads.length === 0 && !filterToken) {
    return (
      <div className="text-center py-12 text-gray-400">
        <CloudArrowUpIcon className="w-12 h-12 mx-auto mb-3 opacity-40" />
        <p>No files have been uploaded yet.</p>
        <p className="text-sm mt-1">
          Create a token and share the link with a vendor to get started.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Token filter */}
      <div className="mb-3 flex items-center gap-2">
        <input
          type="text"
          value={filterToken}
          onChange={(e) => setFilterToken(e.target.value)}
          placeholder="Filter by token…"
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-500"
        />
        {filterToken && (
          <button
            onClick={() => setFilterToken('')}
            className="text-sm text-gray-400 hover:text-gray-600"
          >
            Clear
          </button>
        )}
      </div>

      {uploads.length === 0 ? (
        <div className="text-center py-8 text-gray-400 text-sm">
          No uploads found for this token.
        </div>
      ) : (
        <div className="overflow-x-auto bg-gt-white rounded-xl shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-gray-500">
                <th className="px-4 py-3 font-medium">File</th>
                <th className="px-4 py-3 font-medium">Vendor</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Uploaded</th>
                <th className="px-4 py-3 font-medium">Parts</th>
                <th className="px-4 py-3 font-medium">Link</th>
              </tr>
            </thead>
            <tbody>
              {uploads.map((u) => {
                const cfg = STATUS_CONFIG[u.status] || STATUS_CONFIG.initiated;
                const StatusIcon = cfg.icon;
                const cdnUrl = u.cdnUrl || u.location;
                return (
                  <tr
                    key={u.id}
                    className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <DocumentIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <p
                            className="font-medium text-gray-800 truncate max-w-[200px]"
                            title={u.fileName}
                          >
                            {u.fileName || 'Unknown'}
                          </p>
                          <p className="text-xs text-gray-400 truncate max-w-[200px]" title={u.s3Key}>
                            {u.s3Key}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-gray-700">{u.vendorName || '—'}</p>
                      <p className="text-xs text-gray-400" title={u.token}>
                        {shortenToken(u.token)}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.color}`}
                      >
                        <StatusIcon className="w-3.5 h-3.5" />
                        {cfg.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      <p>{formatDate(u.completedAt || u.initiatedAt)}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {u.partCount || '—'}
                    </td>
                    <td className="px-4 py-3">
                      {cdnUrl ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => onCopyLink(cdnUrl)}
                            className="p-1 text-teal-500 hover:bg-teal-50 rounded transition-colors"
                            title="Copy link"
                          >
                            <LinkIcon className="w-4 h-4" />
                          </button>
                          <a
                            href={cdnUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
                            title="Open"
                          >
                            <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                          </a>
                          {copiedLink === cdnUrl && (
                            <span className="text-xs text-green-600">Copied!</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tokens table
// ---------------------------------------------------------------------------
function TokensTable({ tokens, onDeactivate, onCopyLink, copiedLink }) {
  if (tokens.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400">
        <UserGroupIcon className="w-12 h-12 mx-auto mb-3 opacity-40" />
        <p>No access tokens created yet.</p>
        <p className="text-sm mt-1">
          Click "New Token" to create one and share it with a vendor.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto bg-gt-white rounded-xl shadow-sm">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-left text-gray-500">
            <th className="px-4 py-3 font-medium">Vendor</th>
            <th className="px-4 py-3 font-medium">Description</th>
            <th className="px-4 py-3 font-medium">Status</th>
            <th className="px-4 py-3 font-medium">Created</th>
            <th className="px-4 py-3 font-medium">Upload Link</th>
            <th className="px-4 py-3 font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {tokens.map((t) => {
            const isActive = t.active !== false;
            const uploadLink = getVendorUploadLink(t.uploadLink || t.token);
            return (
              <tr
                key={t.id || t.token}
                className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
              >
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-800">{t.vendorName || '—'}</p>
                  <p className="text-xs text-gray-400">{shortenToken(t.token)}</p>
                </td>
                <td className="px-4 py-3 text-gray-600">
                  {t.description || '—'}
                </td>
                <td className="px-4 py-3">
                  {isActive ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-600">
                      <CheckCircleIcon className="w-3.5 h-3.5" />
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                      <XCircleIcon className="w-3.5 h-3.5" />
                      Inactive
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-600">
                  {formatDate(t.createdAt)}
                </td>
                <td className="px-4 py-3">
                  {uploadLink ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => onCopyLink(uploadLink)}
                        className="p-1 text-teal-500 hover:bg-teal-50 rounded transition-colors"
                        title="Copy link"
                      >
                        <LinkIcon className="w-4 h-4" />
                      </button>
                      <a
                        href={uploadLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
                        title="Open"
                      >
                        <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                      </a>
                      {copiedLink === uploadLink && (
                        <span className="text-xs text-green-600">Copied!</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {isActive && (
                    <PermissionGate permission="admin.vendor_uploads.manage_tokens"><button
                      onClick={() => onDeactivate(t.token)}
                      className="text-xs text-red-500 hover:bg-red-50 px-2 py-1 rounded transition-colors"
                    >
                      Deactivate
                    </button></PermissionGate>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create token modal
// ---------------------------------------------------------------------------
function CreateTokenModal({ onClose, onCreated }) {
  const [vendorName, setVendorName] = useState('');
  const [description, setDescription] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!vendorName.trim()) {
      setError('Vendor name is required.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await createVendorToken({
        vendorName: vendorName.trim(),
        description: description.trim(),
        expiresAt: expiresAt || undefined,
      });
      onCreated({
        vendorName: vendorName.trim(),
        uploadLink: getVendorUploadLink(result.uploadLink || result.token),
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">
            Create Upload Token
          </h3>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Vendor Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={vendorName}
              onChange={(e) => setVendorName(e.target.value)}
              placeholder="e.g. VideoCo Studios"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this upload for?"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Expiry Date (optional)
            </label>
            <input
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            <p className="text-xs text-gray-400 mt-1">
              Token will stop working after this date.
            </p>
          </div>
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg p-2">
              <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 text-sm font-semibold text-white bg-teal-500 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {loading ? 'Creating…' : 'Create Token'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
